import { useRef } from 'react';
import {
  ContentfulLivePreviewProvider,
  useContentfulLiveUpdates,
} from '@contentful/live-preview/react';
import _get from 'lodash/get';

// components
import { CmsComponentSwitch } from 'src/cms/CmsComponentSwitch';
import { PaywallProvider } from 'src/components/Surfaces/Paywall/PaywallProvider';
import {
  collectInvertedPaywallSecretKeys,
  collectPaywallSecretKeys,
  usePaywall,
} from 'src/components/Surfaces/Paywall/usePaywall';
import {
  ArticleTemplate,
  BlogPostTemplate,
  EventPageTemplate,
  LandingPageTemplate,
  MainTemplate,
  PartnerDirectoryTemplate,
  PartnerPageTemplate,
  ProductPageTemplate,
  UseCaseTemplate,
  homepageMetadataAdapter,
  mainTemplateMetaTagAdapter,
  mainTemplateStylesAdapter,
} from 'src/templates';
import {
  mainTemplateFooterAdapter,
  mainTemplateNavigationAdapter,
} from 'src/templates/MainTemplate/adapters';

//localized pages
import FourOhFour from 'src/pages/404';
import Contact from 'src/pages/contact';
import ContactThanks from 'src/pages/contact-thanks';
import DeveloperPolicy from 'src/pages/developer-policy';
import DiscoverApps from 'src/pages/discover-apps';
import HowItWorks from 'src/pages/how-it-works-for-consumers';
import HowWeHandleData from 'src/pages/how-we-handle-data';
import OpenBanking from 'src/pages/open-banking';
import WhatIsPlaid from 'src/templates/what-is-plaid';
import WhyPlaidIsInvolved from 'src/pages/why-is-plaid-involved';

// helpers
import {
  Contentful,
  ContentfulTemplates,
  StaticTemplates,
  isDevelopment,
  isProduction,
  isTesting,
} from 'src/lib/constants';
import {
  ContentfulSystemMetadata,
  TemplateFieldShape,
} from 'src/lib/prop-types';
import { getLocale } from 'src/lib/utils';

const propTypes = {
  fields: TemplateFieldShape,
  sys: ContentfulSystemMetadata,
};

const Switch = (props) => {
  const templateType = _get(
    props,
    Contentful.CONTENT_TYPE_ID,
    props?.params?.template, // fallback to hard coded template for static localized pages
  );
  const url = props?.fields?.url;
  const isHomepage = url
    ? url.every((p) => {
        return p === ContentfulTemplates.HOMEPAGE;
      })
    : false;
  const locale = getLocale(props);
  const pageComponents = props?.fields?.components ?? [];
  const { paywallComponent } = usePaywall(pageComponents);
  // A paywall linked from an embedded video card is not in the component list,
  // so it has no page-level entry. Settings still needs a provider, and a key
  // to clear, or Reset Paywall never appears.
  const paywallKeys = collectPaywallSecretKeys(pageComponents);
  const invertedPaywallKeys = collectInvertedPaywallSecretKeys(pageComponents);
  const unlockKey = paywallComponent?.fields?.secretKey || paywallKeys[0];
  // A video gate is not the page paywall entry. Its invert flag still has to
  // drive the provider that Contentful settings resets, or Reset writes the
  // lock sentinel and the video stays locked.
  const hasInvertedPaywallLogic =
    Boolean(paywallComponent?.fields?.invertLogic) ||
    invertedPaywallKeys.includes(unlockKey);

  switch (templateType) {
    case ContentfulTemplates.LANDING_PAGE:
      return <LandingPageTemplate {...props} locale={locale} />;
    case ContentfulTemplates.PARTNER_DIRECTORY:
      return <PartnerDirectoryTemplate {...props} />;
    case ContentfulTemplates.PARTNER_PAGE:
      return <PartnerPageTemplate {...props} />;
    case ContentfulTemplates.USE_CASE:
      return <UseCaseTemplate {...props} locale={locale} />;
    case ContentfulTemplates.BLOG_POST:
      return <BlogPostTemplate {...props} />;
    case ContentfulTemplates.PRODUCT_PAGE:
      return <ProductPageTemplate {...props} locale={locale} />;
    case ContentfulTemplates.EVENT:
      return <EventPageTemplate {...props} />;
    case ContentfulTemplates.ARTICLE:
      return <ArticleTemplate {...props} locale={locale} />;
    case StaticTemplates.WHAT_IS_PLAID:
      return <WhatIsPlaid {...props} locale={locale} />;
    case StaticTemplates.WHY_PLAID_IS_INVOLVED:
      return <WhyPlaidIsInvolved {...props} locale={locale} />;
    case StaticTemplates.CONTACT_THANKS:
      return <ContactThanks {...props} locale={locale} />;
    case StaticTemplates.CONTACT:
      return <Contact {...props} locale={locale} />;
    case StaticTemplates.DEVELOPER_POLICY:
      return <DeveloperPolicy {...props} locale={locale} />;
    case StaticTemplates.DISCOVER_APPS:
      return <DiscoverApps {...props} locale={locale} />;
    case StaticTemplates.HOW_IT_WORKS:
      return <HowItWorks {...props} locale={locale} />;
    case StaticTemplates.HOW_WE_HANDLE_DATA:
      return <HowWeHandleData {...props} locale={locale} />;
    case StaticTemplates.OPEN_BANKING:
      return <OpenBanking {...props} locale={locale} />;
    case ContentfulTemplates.PAGE: {
      const page = (
        <MainTemplate
          cmsTheme={props?.fields?.theme || props?.fields?.cmsTheme}
          locale={locale}
          sys={props?.sys}
          {...mainTemplateNavigationAdapter(props)}
          {...mainTemplateFooterAdapter(props)}
          {...mainTemplateStylesAdapter(props)}
          {...mainTemplateMetaTagAdapter(props)}
          {...(isHomepage && homepageMetadataAdapter())}
        >
          <CmsComponentSwitch
            components={props?.fields?.components}
            templatePage={ContentfulTemplates.PAGE}
          />
        </MainTemplate>
      );
      // Secret key is unique per paywall entry, so reusing the entry shares
      // the unlock and a new entry stays locked on its own.
      if (!unlockKey) {
        return page;
      }
      return (
        <PaywallProvider
          cookieName={unlockKey}
          unlockKey={unlockKey}
          resetKeys={paywallKeys}
          invertedResetKeys={invertedPaywallKeys}
          hasInvertedPaywallLogic={hasInvertedPaywallLogic}
        >
          {page}
        </PaywallProvider>
      );
    }
    default:
      if (isDevelopment || isTesting) {
        return (
          <>
            <p>
              <b>Error:</b> No template matching&nbsp;
              <b>
                <i>{templateType}</i>
              </b>
            </p>
            <p>How can you fix this?</p>
            <ul>
              <li>
                Check that all required fields are filled in, for example, meta
                title, description, url, and at least one valid component
              </li>
              <li>
                Sometimes this happens due to a bad preview cookie. Clear the{' '}
                <b>__next_preview_data</b> cookie and try refreshing your
                browser.
              </li>
            </ul>
          </>
        );
      } else {
        return <FourOhFour />;
      }
  }
};

const TemplateSwitch = (staticProps) => {
  const liveProps = useContentfulLiveUpdates(staticProps);
  const lastGoodPropsRef = useRef(null);

  // Prefer live props, fall back to static when hook hasn't populated (e.g. HMR)
  const propsFromSource =
    _get(liveProps, Contentful.CONTENT_TYPE_ID, liveProps?.params?.template) !=
    null
      ? liveProps
      : { ...staticProps, ...liveProps };

  const templateType = _get(
    propsFromSource,
    Contentful.CONTENT_TYPE_ID,
    propsFromSource?.params?.template,
  );

  // During Next.js HMR both staticProps and liveProps can be briefly empty.
  // Keep rendering the last known good props so we don't flash the "no template" UI.
  if (templateType != null) {
    lastGoodPropsRef.current = propsFromSource;
  }
  const props =
    templateType != null ? propsFromSource : lastGoodPropsRef.current;

  // NOTE: while developing locally and using the live preview mode sometimes your page
  // will not load when you manually navigate to it. If this happens the solution (for now) is to delete
  // the next.js preview cookie
  return (
    <ContentfulLivePreviewProvider
      locale={props?.sys?.locale || 'en-US'}
      enableInspectorMode={!isProduction || !!staticProps?.preview}
      enableLiveUpdates={!isProduction || !!staticProps?.preview}
    >
      <Switch {...(props || staticProps)} />
    </ContentfulLivePreviewProvider>
  );
};
TemplateSwitch.propTypes = propTypes;

export default TemplateSwitch;
